// @flow
import React, { type Node } from 'react';
import { FormattedMessage } from 'react-intl';
import Field from 'brastrap/common/field/Field';
import { applyModifiers } from '../../utils';

const invalidInputProps = [
  'initialValue',
  'autofill',
  'onUpdate',
  'valid',
  'invalid',
  'dirty',
  'pristine',
  'error',
  'active',
  'visited',
  'autofilled',
  'modifiers',
  'floatLabel',
  'note',
  'touched',
  'showCharacterCount',
  'intl',
  'description',
  'fieldType',
  'validation',
];

type InputTypes =
  | 'email'
  | 'number'
  | 'password'
  | 'search'
  | 'tel'
  | 'text'
  | 'url';

export type Props = {
  disabled?: boolean,
  floatLabel?: boolean,
  label?: string,
  maxLength?: number,
  modifiers?: Modifiers,
  name: string,
  note?: Node,
  placeholder?: string,
  touched?: boolean,
  showCharacterCount?: boolean,
  required?: boolean,
  type: InputTypes,
  value?: string,
  'aria-label'?: string,
  isSearch?: boolean,
  disableFieldIcons?: boolean,
  disableStateIcons?: boolean,
  autoComplete?: boolean,
};

/**
 * Ensure only valid input properties are used.
 *
 * @param {object} props
 * @return {object}
 */
export const filterInputProps = (props: {}) => {
  const inputProps = { ...props };
  invalidInputProps.forEach(attr => delete inputProps[attr]);
  if (inputProps.value === null) {
    delete inputProps.value;
  }
  return inputProps;
};

/**
 * @param {Object} props
 * @constructor
 */
const CharsLeft = ({ number }: { number: number }) => (
  <em className="c-field__note c-field__note--character-count">
    <strong>{number}</strong>
    &nbsp;
    <FormattedMessage
      id="field-input.character-count"
      defaultMessage="characters remaining"
    />
  </em>
);

/**
 * @param {object} props
 * @return {XML}
 * @constructor
 */
const FieldInput = (props: Props) => {
  const className = applyModifiers('c-field-input', props.modifiers);
  let stateIconClass = '';
  let fieldIconClass = '';
  const inputProps = filterInputProps(props);
  const hasFieldIcon =
    !props.disableFieldIcons &&
    (props.isSearch ||
      props.type === 'email' ||
      props.type === 'tel' ||
      props.type === 'password');

  if (!props.disableStateIcons && props.note && props.touched) {
    stateIconClass = 'c-field__warning-icon';
  }

  if (
    !props.disableStateIcons &&
    !props.note &&
    props.touched &&
    props.value &&
    props.value.length > 0
  ) {
    stateIconClass = 'c-field__completed-icon';
  }

  if (props.isSearch && !props.disableFieldIcons) {
    fieldIconClass += ' c-field__search-icon';
  }

  if (props.type === 'email' && !props.disableFieldIcons) {
    fieldIconClass += ' c-field__email-icon';
  }

  if (props.type === 'tel' && !props.disableFieldIcons) {
    fieldIconClass += ' c-field__phone-icon';
  }

  if (props.type === 'password' && !props.disableFieldIcons) {
    fieldIconClass += ' c-field__password-icon';
  }

  const inputStyling = {
    ...(hasFieldIcon ? { paddingLeft: '2.75rem' } : {}),
    ...(!props.disableStateIcons ? { paddingRight: '2.5rem' } : {}),
  };

  const inputField = (
    <div
      className={`c-field-input-wrapper ${fieldIconClass} ${stateIconClass}`}
    >
      <input {...inputProps} className={className} style={inputStyling} />
    </div>
  );

  if (props.label) {
    return (
      <Field {...props}>
        {inputField}
        {props.note && props.touched && (
          <em className="c-field__note c-field__note--right-align">
            {props.note}
          </em>
        )}
        {props.maxLength && props.showCharacterCount && (
          <CharsLeft number={props.maxLength - (props.value || '').length} />
        )}
      </Field>
    );
  }
  return inputField;
};

FieldInput.defaultProps = {
  type: 'text',
  isSearch: false,
};

export default FieldInput;
